import { IConfiguratorAnswers } from '../models/configurator.model';
import { isEmptyAnswer } from './configurator-value.helper';

/**
 * Answers can be nested: `guestWc.size` or `bathrooms.1.shower` (array index as a path segment).
 * These helpers read and write such paths without mutating the answers object.
 */

const splitPath = (path: string): string[] => path.split('.');

const isIndex = (segment: string): boolean => /^\d+$/.test(segment);

export const getAnswer = (answers: IConfiguratorAnswers, path: string): unknown =>
  splitPath(path).reduce<unknown>(
    (value, segment) => (value && typeof value === 'object' ? (value as any)[segment] : undefined),
    answers
  );

/**
 * Returns new answers with `value` at `path`. Empty values remove the key; objects left empty are removed too,
 * while arrays keep their positions (an empty item stays `{}` so the following items do not move).
 */
export const setAnswer = (
  answers: IConfiguratorAnswers,
  path: string,
  value: unknown
): IConfiguratorAnswers => setAt(answers, splitPath(path), value) as IConfiguratorAnswers;

const setAt = (container: unknown, [segment, ...rest]: string[], value: unknown): unknown => {
  const useArray = Array.isArray(container) || (container === undefined && isIndex(segment));
  const copy: any = useArray ? [...((container as unknown[]) || [])] : { ...((container as object) || {}) };

  const next = rest.length ? setAt(copy[segment], rest, value) : value;

  if (useArray) {
    copy[Number(segment)] = isEmptyAnswer(next) ? {} : next;
    // Writing behind the end must not leave holes (they would be saved as null).
    for (let index = 0; index < copy.length; index++) {
      if (copy[index] === undefined) copy[index] = {};
    }
    return trimTrailingEmpty(copy);
  }

  if (isEmptyAnswer(next)) {
    delete copy[segment];
  } else {
    copy[segment] = next;
  }
  return copy;
};

const trimTrailingEmpty = (items: unknown[]): unknown[] => {
  const trimmed = [...items];
  while (trimmed.length && isEmptyAnswer(trimmed[trimmed.length - 1])) trimmed.pop();
  return trimmed;
};
